import type { Uuid } from "spacetimedb";
import { useMemo, useState } from "react";

import {
  Drawer,
  DrawerCloseIcon,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/web/components/primitives/drawer";
import {
  activeBusinessCard,
  presentationFromContext,
  ShareBusinessCardDrawerBody,
} from "@/web/routes/_protected/smb/-dashboard/-actions/share/ShareBusinessCardAction";
import { CompletedActionCard } from "@/web/routes/_protected/smb/-dashboard/-CompletedActionCard";
import { useSuspendedShareBusinessCardContexts } from "@/web/routes/_protected/smb/-dashboard/dashboardData";
import { useSuspendedSmbSuperView } from "@/web/routes/_protected/smb/-dashboard/superView";
import type { SmbActionDefinition } from "@/web/routes/_protected/smb/-dashboard/-types";

type Props = {
  underwritableEntity: Uuid;
};

/**
 * Completed-side share card. Reopens the same drawer the active card uses so
 * the user can re-share (or rotate the access code) after the first send.
 * Mirrors the pattern in `-footer/FundraiseTile.tsx`: subscribe to the
 * contexts view, build the `presentation`, host a local drawer.
 */
function ShareBusinessCardCompleted({ underwritableEntity }: Props) {
  const [open, setOpen] = useState(false);
  const shareContexts = useSuspendedShareBusinessCardContexts();
  const shareContext = useMemo(
    () => shareContexts.find((row) => row.underwritableEntity.compareTo(underwritableEntity) === 0),
    [shareContexts, underwritableEntity],
  );
  const presentation = useMemo(() => presentationFromContext(shareContext), [shareContext]);

  // The completed-side card only renders once a card has been shared; resolve
  // the live row the "Update" flow edits.
  const smbInfo = useSuspendedSmbSuperView(underwritableEntity);
  const existingCard = useMemo(
    () => activeBusinessCard(smbInfo.businessCards),
    [smbInfo.businessCards],
  );

  return (
    <>
      <CompletedActionCard
        category="fundraise"
        badge="Fundraise"
        title="Share with your community"
        action={{
          label: "Share",
          onClick: () => setOpen(true),
        }}
      />
      <Drawer open={open} onOpenChange={setOpen}>
        <DrawerContent>
          <DrawerHeader>
            <DrawerTitle>Share with potential investors</DrawerTitle>
            <DrawerCloseIcon />
          </DrawerHeader>
          {/* This rule only fires once a card exists, so `existingCard` is
              always present here — guard anyway to satisfy the required prop. */}
          {existingCard ? (
            <ShareBusinessCardDrawerBody
              presentation={presentation}
              existingCard={existingCard}
              onClose={() => setOpen(false)}
              onComplete={() => setOpen(false)}
            />
          ) : null}
        </DrawerContent>
      </Drawer>
    </>
  );
}

export function createShareBusinessCardCompleted(
  props: Props & { id?: string },
): SmbActionDefinition {
  const { underwritableEntity, id = `share-profile:done:${underwritableEntity.toString()}` } =
    props;
  return {
    id,
    category: "fundraise",
    canSkip: false,
    render: () => <ShareBusinessCardCompleted underwritableEntity={underwritableEntity} />,
  };
}
